import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import WorkflowArticle from './WorkflowArticle';
import './ResearchPage.css';

const PILLARS = [
  { key: 'all',     label: 'All' },
  { key: 'ai',      label: 'AI' },
  { key: 'system',  label: 'System' },
  { key: 'feature', label: 'Features' },
];

const PILLAR_LABELS = { ai: 'AI', system: 'System', feature: 'Feature' };

const DOCS = [
  { key: 'benchmarks',           label: 'Benchmarks' },
  { key: 'production-readiness', label: 'Production Readiness' },
  { key: 'context',              label: 'Context' },
];

function parseDate(iso) {
  const [datePart] = iso.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(iso) {
  const d = parseDate(iso);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function monthKey(iso) {
  const d = parseDate(iso);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function groupByMonth(entries) {
  const groups = {};
  entries.forEach(e => {
    const k = monthKey(e.publishedAt);
    if (!groups[k]) groups[k] = [];
    groups[k].push(e);
  });
  return groups;
}

function FeedCard({ entry }) {
  return (
    <article className={`rf-card rf-card--${entry.pillar}`}>
      <div className="rf-card-meta">
        <span className={`rf-badge rf-badge--${entry.pillar}`}>{PILLAR_LABELS[entry.pillar]}</span>
        <span className="rf-card-date">{formatDate(entry.publishedAt)}</span>
      </div>
      <h3 className="rf-card-title">{entry.title}</h3>
      <p className="rf-card-body">{entry.body}</p>
      {entry.metrics && entry.metrics.length > 0 && (
        <div className="rf-metrics">
          {entry.metrics.map((m, i) => (
            <div key={i} className="rf-metric">
              <span className="rf-metric-label">{m.label}</span>
              <div className="rf-metric-values">
                <span className="rf-metric-before">{m.before}</span>
                <span className="rf-metric-arrow">→</span>
                <span className="rf-metric-after">{m.after}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function ProgressFeed() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pillar, setPillar] = useState('all');

  useEffect(() => {
    fetch('/api/research/feed')
      .then(r => r.json())
      .then(d => { setEntries(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = pillar === 'all' ? entries : entries.filter(e => e.pillar === pillar);
  const grouped = groupByMonth(filtered);

  return (
    <div className="rf-feed">
      <div className="rf-feed-header">
        <div>
          <h2 className="rf-feed-title">Progress Log</h2>
          <p className="rf-feed-subtitle">Every meaningful improvement to ferl — AI, infrastructure, and features.</p>
        </div>
        <div className="rf-pillar-filters">
          {PILLARS.map(p => (
            <button
              key={p.key}
              className={`rf-filter-btn${pillar === p.key ? ' active' : ''}${p.key !== 'all' ? ` rf-filter-btn--${p.key}` : ''}`}
              onClick={() => setPillar(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="rf-loading">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <p className="rf-empty">No entries yet for this category.</p>
      )}

      {Object.entries(grouped).map(([month, items]) => (
        <div key={month} className="rf-month-group">
          <h4 className="rf-month-label">{month}</h4>
          {items.map(e => <FeedCard key={e.id} entry={e} />)}
        </div>
      ))}
    </div>
  );
}

function TechnicalDocs() {
  const [active, setActive] = useState('benchmarks');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/research/${active}`)
      .then(r => r.json())
      .then(d => { setContent(d.markdown || ''); setLoading(false); })
      .catch(() => { setContent('Could not load document.'); setLoading(false); });
  }, [active]);

  return (
    <div className="rf-docs">
      <div className="rf-docs-nav">
        {DOCS.map(d => (
          <button
            key={d.key}
            className={active === d.key ? 'active' : ''}
            onClick={() => setActive(d.key)}
          >
            {d.label}
          </button>
        ))}
        <Link to="/research/thesis-engine" className="rf-docs-engine-link">
          Market Thesis Engine →
        </Link>
      </div>
      <main className="research-content">
        {loading ? (
          <p className="research-loading">Loading…</p>
        ) : (
          <ReactMarkdown>{content}</ReactMarkdown>
        )}
      </main>
    </div>
  );
}

function ComingSoon() {
  return <p className="research-loading" style={{ marginTop: 48 }}>Coming soon.</p>;
}

export default function ResearchPage() {
  const [tab, setTab] = useState('workflow');

  return (
    <div className="research-page">
      <header className="research-header">
        <div className="rf-header-top">
          <div>
            <h1>Research</h1>
            <p className="rf-header-sub">How ferl is being built — transparently.</p>
          </div>
          <div className="rf-tab-switcher">
            <button className={tab === 'workflow' ? 'active' : ''} onClick={() => setTab('workflow')}>How It's Built</button>
            <button className={tab === 'feed' ? 'active' : ''} onClick={() => setTab('feed')}>Progress</button>
            <button className={tab === 'docs' ? 'active' : ''} onClick={() => setTab('docs')}>Technical Docs</button>
          </div>
        </div>
      </header>

      {tab === 'workflow' && <WorkflowArticle />}
      {tab === 'feed' && <ComingSoon />}
      {tab === 'docs' && <ComingSoon />}
    </div>
  );
}
